export type ParsedUserAgent = {
  browserName: string | null;
  browserVersion: string | null;
  osName: string | null;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'bot' | 'unknown';
};

export type ClientSecurityContext = {
  userAgent: string | null;
  browserName: string | null;
  browserVersion: string | null;
  osName: string | null;
  deviceType: ParsedUserAgent['deviceType'];
  timezone: string | null;
  language: string | null;
  requestPath: string | null;
};

type AuditClientLike = {
  user_agent?: string | null;
  browser_name?: string | null;
  browser_version?: string | null;
  os_name?: string | null;
  device_type?: string | null;
};

const BROWSER_RULES: Array<{
  name: string;
  pattern: RegExp;
}> = [
  { name: 'Microsoft Edge', pattern: /(edg|edga|edgios)\/([\d.]+)/i },
  { name: 'Opera', pattern: /(opr|opera|opios)\/([\d.]+)/i },
  { name: 'Samsung Internet', pattern: /samsungbrowser\/([\d.]+)/i },
  { name: 'Mozilla Firefox', pattern: /(firefox|fxios)\/([\d.]+)/i },
  { name: 'Google Chrome', pattern: /(chrome|crios)\/([\d.]+)/i },
  { name: 'Safari', pattern: /version\/([\d.]+).*safari/i },
  { name: 'Internet Explorer', pattern: /msie\s([\d.]+)/i },
  { name: 'Internet Explorer', pattern: /trident\/.*rv:([\d.]+)/i },
];

const OS_RULES: Array<{
  name: string;
  pattern: RegExp;
}> = [
  { name: 'Windows Phone', pattern: /windows phone/i },
  { name: 'Android', pattern: /android/i },
  { name: 'iPadOS', pattern: /ipad/i },
  { name: 'iOS', pattern: /iphone|ipod/i },
  { name: 'macOS', pattern: /mac os x|macintosh/i },
  { name: 'Windows', pattern: /windows/i },
  { name: 'ChromeOS', pattern: /cros/i },
  { name: 'Linux', pattern: /linux/i },
];

export function parseUserAgent(userAgent?: string | null): ParsedUserAgent {
  const ua = (userAgent || '').trim();
  if (!ua) {
    return {
      browserName: null,
      browserVersion: null,
      osName: null,
      deviceType: 'unknown',
    };
  }

  const lower = ua.toLowerCase();

  let browserName: string | null = null;
  let browserVersion: string | null = null;
  for (const rule of BROWSER_RULES) {
    const match = ua.match(rule.pattern);
    if (match) {
      browserName = rule.name;
      browserVersion = match[2] || match[1] || null;
      break;
    }
  }

  if (!browserName) {
    if (/bot|crawler|spider|curl|wget|postmanruntime/i.test(ua)) {
      browserName = 'Bot veya istemci';
    } else {
      browserName = 'Bilinmeyen tarayıcı';
    }
  }

  let osName: string | null = null;
  for (const rule of OS_RULES) {
    if (rule.pattern.test(ua)) {
      osName = rule.name;
      break;
    }
  }

  let deviceType: ParsedUserAgent['deviceType'] = 'desktop';
  if (/bot|crawler|spider|curl|wget|postmanruntime/i.test(ua)) {
    deviceType = 'bot';
  } else if (/ipad|tablet|sm-t|tab/i.test(lower)) {
    deviceType = 'tablet';
  } else if (
    /mobile|iphone|ipod|android|windows phone/i.test(lower) &&
    !/ipad|tablet/i.test(lower)
  ) {
    deviceType = 'mobile';
  }

  return {
    browserName,
    browserVersion,
    osName,
    deviceType,
  };
}

export function getClientSecurityContext(): ClientSecurityContext {
  if (typeof window === 'undefined') {
    return {
      userAgent: null,
      browserName: null,
      browserVersion: null,
      osName: null,
      deviceType: 'unknown',
      timezone: null,
      language: null,
      requestPath: null,
    };
  }

  const userAgent = window.navigator.userAgent || null;
  const parsed = parseUserAgent(userAgent);

  return {
    userAgent,
    browserName: parsed.browserName,
    browserVersion: parsed.browserVersion,
    osName: parsed.osName,
    deviceType: parsed.deviceType,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    language: window.navigator.language || null,
    requestPath:
      typeof window.location !== 'undefined'
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : null,
  };
}

export function formatDeviceType(deviceType?: string | null) {
  switch (deviceType) {
    case 'desktop':
      return 'Masaüstü';
    case 'mobile':
      return 'Mobil';
    case 'tablet':
      return 'Tablet';
    case 'bot':
      return 'Bot veya API istemcisi';
    default:
      return 'Bilinmeyen cihaz';
  }
}

export function formatCountryCode(countryCode?: string | null) {
  if (!countryCode) return 'Konum bilinmiyor';
  const normalized = countryCode.toUpperCase();
  if (normalized === 'TR') return 'Türkiye';

  try {
    if (typeof Intl !== 'undefined' && 'DisplayNames' in Intl) {
      const displayNames = new Intl.DisplayNames(['tr'], { type: 'region' });
      return displayNames.of(normalized) || normalized;
    }
  } catch {
    // ignore
  }

  return normalized;
}

export function formatAuditClientSummary(log: AuditClientLike) {
  const parsed =
    log.browser_name || log.os_name || log.device_type
      ? {
          browserName: log.browser_name || null,
          browserVersion: log.browser_version || null,
          osName: log.os_name || null,
          deviceType: (log.device_type as ParsedUserAgent['deviceType']) || 'unknown',
        }
      : parseUserAgent(log.user_agent);

  const browserPart = parsed.browserVersion
    ? `${parsed.browserName} ${parsed.browserVersion}`
    : parsed.browserName || 'Bilinmeyen tarayıcı';
  const osPart = parsed.osName || 'Bilinmeyen işletim sistemi';
  const devicePart = formatDeviceType(parsed.deviceType);

  return `${browserPart} / ${osPart} / ${devicePart}`;
}
